import {
    faClockRotateLeft,
    faLocationDot,
    faSuitcaseRolling,
} from '@fortawesome/pro-regular-svg-icons';
import {
    type LocationGroup,
    SearchFromErrorMessage,
    type UrlStringParams,
} from '../types';
import type { BundlePathData, CityMappingModel } from './types';
import BundlePriceLoader from './BundlePriceLoader';
import { type BundleUrlParams } from '@utils/bundle';
import ItinerarySearchForm from '@search/ItinerarySearchForm';
import { type FlightSearchForm } from '@search/trip/flight/FlightSearchForm';
import { type HotelSearchForm } from '@search/hotel/HotelSearchForm';
import {
    type Location,
    type DateSpanObject,
    PaxType,
    type PaxCount,
    type ReserveInfo,
} from '@utils/types';
import { ReserveType, VerifyPathError } from '@utils/itinerary';
import type { AnalyticsAttribute } from '@store/analytics';

const popularCities = [
    {
        ID: 682,
        Title: 'اصفهان',
    },
    {
        ID: 711,
        Title: 'تهران',
    },
    {
        ID: 738,
        Title: 'مشهد ',
    },
    {
        ID: 748,
        Title: 'اهواز',
    },
    {
        ID: 784,
        Title: 'شیراز',
    },
];

export class BundleSearchForm extends ItinerarySearchForm<
    Location,
    BundlePathData
> {
    _origin: Location | null = null;
    _destination: Location | null = null;
    originSearchKeyword = '';
    destinationSearchKeyword = '';
    fromDate: Date | null = null;
    toDate: Date | null = null;
    lastSearches: Location[] = [];
    override get destination(): Location | null {
        return this._destination;
    }

    setOrigin(location: Location | null) {
        this._origin = location;
    }

    override get searchReady(): boolean {
        return !!(
            this.fromDate &&
            this.toDate &&
            this.origin &&
            this.destination
        );
    }

    get origin() {
        return this._origin;
    }

    allLocations: Location[] = [];

    override priceLoader: BundlePriceLoader | undefined = undefined;

    override verifyViewPath(): Promise<void> {
        return Promise.resolve();
    }

    override loadViewUrlParams(params: BundleUrlParams): Promise<void> {
        if (!this.origin || !this.destination) {
            const origin = this.allLocations.find(
                l => l.code === params.originCode
            );
            const destination = this.allLocations.find(
                l => l.code === params.destinationCode
            );
            if (origin && destination) {
                this._origin = origin;
                this._destination = destination;
            } else {
                throw new VerifyPathError('شهر مورد نظر یافت نشد.', 404);
            }
        }
        this._origin = this.allLocations.find(
            l => l.code === params.originCode
        )!;
        this._destination = this.allLocations.find(
            l => l.code === params.destinationCode
        )!;
        this.fromDate = params.fromDate;
        this.toDate = params.toDate;
        this.roomsPaxCounts = this.mapQueryRoomsToPaxes(params.rooms);
        return Promise.resolve();
    }

    get residencePageUrl(): string {
        return this.viewUrl.replace('/tour', '/tour/residence');
    }

    getFlightForm(): FlightSearchForm {
        return useSearchFormStore().bundleFlightForm as FlightSearchForm;
    }

    getHotelForm(): HotelSearchForm {
        return useSearchFormStore().bundleHotelForm as HotelSearchForm;
    }

    override nextDay(): void {
        throw new Error('not implemented');
    }

    override prevDay(): void {
        throw new Error('not implemented');
    }

    getFormBaseOnReserveType(
        reserveType: ReserveType
    ): HotelSearchForm | FlightSearchForm {
        switch (reserveType) {
            case ReserveType.Hotel:
                return this.getHotelForm();
            case ReserveType.Flight:
                return this.getFlightForm();
            default:
                throw new Error('Reserve type not handled !');
        }
    }

    override get searchAttributes(): AnalyticsAttribute {
        return {
            reserveType: 'hotelAndFlight',
            reserveTypeFa: this.reserveInfo.title,
            from: this.origin?.code.toString(),
            to: this.destination?.code.toString(),
            fromName: this.origin?.title.toString(),
            toName: this.destination?.title.toString(),
            departureDate: dayConfig.gregory(this.fromDate).toDate(),
            returnDate: dayConfig.gregory(this.toDate).toDate(),
            departureDateString: dayConfig.gregory(this.fromDate).toISOString(),
            returnDateString: dayConfig.gregory(this.toDate).toISOString(),
            departureDateFa: this.fromDate
                ? dayConfig.jalali(this.fromDate).format('dddd YYYY/MM/DD')
                : undefined,
            returnDateFa: this.toDate
                ? dayConfig.jalali(this.toDate).format('dddd YYYY/MM/DD')
                : undefined,
            adultCount: `${this.adultCount}`,
            roomCount: `${this.roomsPaxCounts.length}`,
            link: this.viewUrl,
        };
    }

    override async parsePath(
        pathData: BundlePathData
    ): Promise<BundleUrlParams> {
        await this.searchLocations(pathData.origin);
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        const dayAfterTomorrow = new Date();
        dayAfterTomorrow.setDate(tomorrow.getDate() + 1);
        return {
            originCode: pathData.origin,
            destinationCode: pathData.destination,
            fromDate: pathData.from
                ? dayConfig
                      .gregory(pathData.from, {
                          jalali: true,
                          format: formatter(),
                      })
                      .toDate()
                : dayConfig.gregory(tomorrow).toDate(),
            toDate: pathData.to
                ? dayConfig
                      .gregory(pathData.to, {
                          jalali: true,
                          format: formatter(),
                      })
                      .toDate()
                : dayConfig.gregory(dayAfterTomorrow).toDate(),
            rooms: pathData.rooms,
        };
    }

    mapCitiesToLocation(list: CityMappingModel[]): Location[] {
        return list.map(city => ({
            code: `${city.ID}`,
            title: city.Title,
        }));
    }

    get filteredOrigins(): LocationGroup[] {
        if (this.originSearchKeyword) {
            return [
                {
                    title: 'شهرها',
                    icon: faLocationDot,
                    locations: this.filterLocationsBaseOnToken(
                        this.originSearchKeyword,
                        this.allLocations
                    ),
                },
            ];
        } else {
            return [
                ...(this.lastSearches.length
                    ? [
                          {
                              title: 'جستجوهای اخیر',
                              locations: this.lastSearches,
                              icon: faClockRotateLeft,
                              ignoreGroupInSearch: true,
                          },
                      ]
                    : []),
                {
                    title: 'شهرهای پرتردد',
                    locations: this.mapCitiesToLocation(popularCities as any),
                    icon: faLocationDot,
                    ignoreGroupInSearch: true,
                },
            ];
        }
    }

    override get filteredDestinations() {
        if (this.destinationSearchKeyword) {
            return [
                {
                    title: 'شهرها',
                    icon: faLocationDot,
                    locations: this.filterLocationsBaseOnToken(
                        this.destinationSearchKeyword,
                        this.allLocations
                    ),
                },
            ];
        } else {
            return [
                {
                    title: 'شهرهای پرتردد',
                    locations: this.mapCitiesToLocation(popularCities as any),
                    icon: faLocationDot,
                    ignoreGroupInSearch: true,
                },
            ];
        }
    }

    getInitPaxCount(): [PaxCount, PaxCount, PaxCount] {
        return [
            {
                code: PaxType.Adult,
                title: 'بزرگسال',
                subtitle: 'بزرگتر از ۱۲ سال',
                count: 1,
            },
            {
                code: PaxType.Child,
                title: 'کودک',
                subtitle: 'بین ۲ الی ۱۲ سال',
                count: 0,
            },
            {
                code: PaxType.Infant,
                title: 'نوزاد',
                subtitle: 'کوچکتر از ۲ سال',
                count: 0,
            },
        ];
    }

    mapQueryRoomsToPaxes(query: string): [PaxCount, PaxCount, PaxCount][] {
        const rooms = query.match(/(\d_\d_\d)/gi);
        if (!rooms || !rooms.length) {
            return [this.getInitPaxCount()];
        } else {
            return rooms.reduce((rooms: PaxCount[][], c) => {
                const counts = c.match(/\d/gi)!;
                const paxes = this.getInitPaxCount();
                paxes.forEach((p, i) => (p.count = Number(counts[i])));
                return [...rooms, paxes];
            }, []) as [PaxCount, PaxCount, PaxCount][];
        }
    }

    override loadLastSearch() {
        if (this.isLastSearchLoaded) {
            return;
        }
        const lastSearchParamsStr = localStorage.getItem(this.lastSearchKey);
        if (lastSearchParamsStr) {
            const lastSearchParams = JSON.parse(lastSearchParamsStr);
            this.lastSearches = lastSearchParams.lastSearches || [];
            this._origin = lastSearchParams.origin;
            this._destination = lastSearchParams.destination;
            let fromDate = new Date(lastSearchParams.fromDate);
            let toDate = new Date(lastSearchParams.toDate);
            fromDate = dayConfig.gregory(fromDate).isAfter(new Date())
                ? fromDate
                : new Date();
            toDate = dayConfig.gregory(toDate).isAfter(fromDate)
                ? toDate
                : dayConfig.gregory(fromDate).add(1, 'day').toDate();
            this.fromDate = fromDate;
            this.toDate = toDate;
            if (lastSearchParams.rooms) {
                this.roomsPaxCounts = this.mapQueryRoomsToPaxes(
                    lastSearchParams.rooms
                );
            }
        }
        this.isLastSearchLoaded = true;
    }

    override getSearchUrl(): Promise<string> {
        const analyticsStore = useAnalyticsStore();
        const searchFormStore = useSearchFormStore();
        if (process.client) {
            useAnalyticsStore().trackAll(
                'search_tour',
                analyticsStore.searchResultsAttributes
            );
        }
        searchFormStore.firstReserveType = ReserveType.Bundle;

        searchFormStore.setReserveType(ReserveType.Bundle);
        return Promise.resolve(this.viewUrl);
    }

    override initPaxCounts: PaxCount[] = [];

    override get itinerarySearchDates() {
        return {
            start: this.fromDate,
            end: this.toDate,
        };
    }

    override get loadPriceEnabled(): boolean {
        return false;
    }

    removeRoom(i: number) {
        this.roomsPaxCounts.splice(i, 1);
    }

    addRoom() {
        this.roomsPaxCounts.push(this.getInitPaxCount());
    }

    override async loadPrices(payload?: { until: Date }) {
        try {
            if (!this.origin || !this.destination) {
                return;
            }
            const oldLoader = this.priceLoader;
            const newLoader = new BundlePriceLoader({
                from: this.origin.code,
                to: this.destination.code,
                adultCount: this.adultCount,
                childCount: this.childCount,
                infantCount: this.infantCount,
                roomCount: this.getHotelForm().roomCount,
            });
            if (!oldLoader?.equals(newLoader)) {
                this.priceLoader = newLoader;
            }
            await this.priceLoader?.fetchPrices(payload?.until);
        } catch (e) {
            reportException(e);
        }
    }

    override get reserveInfo(): ReserveInfo {
        return {
            type: ReserveType.Bundle,
            title: 'بسته سفر',
            nameE: 'بسته سفر',
            icon: faSuitcaseRolling,
        };
    }

    override async searchLocations(
        searchKeyword: string,
        options: { target: 'origin' | 'destination' } = {
            target: 'origin',
        }
    ) {
        if (options.target === 'origin') {
            this.originSearchKeyword = searchKeyword;
        } else {
            this.destinationSearchKeyword = searchKeyword;
        }
        if (!this.allLocations.length) {
            this.isLoadingLocations = true;
            const list = (await import('@static/city_mappings.js')).default;
            this.allLocations = this.mapCitiesToLocation(
                list.filter(c => c.IATACode != null)
            );
            this.isLoadingLocations = false;
        }
    }

    override setDestination(location: Location | null): void {
        this._destination = location;
    }

    get roomsMaxCheckPaxCounts(): [PaxCount, PaxCount, PaxCount][] {
        return this.roomsPaxCounts.map(value =>
            value.map(p => {
                if (p.code === PaxType.Infant) {
                    return {
                        ...p,
                        max: value.find(e => e.code === PaxType.Adult)!.count,
                    };
                } else {
                    return p;
                }
            })
        ) as [PaxCount, PaxCount, PaxCount][];
    }

    // override get paxCounts(): PaxCount[] {
    //     return this.roomsMaxCheckPaxCounts.reduce(
    //         (all, curr) => [...all, ...curr],
    //         [] as PaxCount[]
    //     );
    // }

    override get searchPaxModel() {
        return this.roomsMaxCheckPaxCounts.map((rp, i) => ({
            title: `اتاق ${i + 1}`,
            deleteAble: i > 0,
            paxList: rp,
        }));
    }

    override get adultCount(): number {
        return this.roomsPaxCounts.reduce(
            (total, paxes) => total + paxes[0].count,
            0
        );
    }

    override get childCount(): number {
        return this.roomsPaxCounts.reduce(
            (total, paxes) => total + paxes[1].count,
            0
        );
    }

    override get infantCount(): number {
        return this.roomsPaxCounts.reduce(
            (total, paxes) => total + paxes[2].count,
            0
        );
    }

    override updatePaxCount({
        count,
        roomIndex,
        type,
    }: {
        type: PaxType;
        count: number;
        roomIndex: number;
    }) {
        const paxCount = this.roomsPaxCounts[roomIndex].find(
            pax => pax.code === type
        );
        if (!paxCount) {
            return;
        }
        paxCount.count = count;
    }

    // override updatePaxCountAndValidate(params: {
    //     type: PaxType;
    //     count: number;
    //     roomIndex: number;
    // }) {
    //     const paxCount = this.roomsPaxCounts[params.roomIndex].find(
    //         pax => pax.code === params.type
    //     );
    //     if (
    //         !paxCount ||
    //         params.count > paxCount.max ||
    //         params.count < paxCount.min
    //     ) {
    //         return;
    //     }
    //     this.updatePaxCount(params);
    // }

    override setItinerarySearchDates(val: DateSpanObject | null): void {
        this.fromDate = val?.start || this.fromDate || new Date();
        this.toDate = val?.end || null;
    }

    override generateViewUrl(params: UrlStringParams<BundleUrlParams>): string {
        const query = new URLSearchParams();
        if (params.fromDate) {
            query.append('from', params.fromDate);
        }
        if (params.toDate) {
            query.append('to', params.toDate);
        }
        if (params.rooms) {
            query.append('rooms', params.rooms);
        }
        const q = query.toString();
        return `/tour/${params.originCode}/${params.destinationCode}${
            q ? `?${q}` : ''
        }`;
    }

    override get viewUrl(): string {
        return this.generateViewUrl({
            originCode: this.origin!.code,
            destinationCode: this.destination!.code,
            fromDate: dayConfig.jalali(this.fromDate).format(formatter()),
            toDate: dayConfig.jalali(this.toDate).format(formatter()),
            rooms: this.mapPaxesToRoomsQuery(this.roomsPaxCounts),
        });
    }

    override get serviceIndexUrl() {
        return '';
    }

    roomsPaxCounts: [PaxCount, PaxCount, PaxCount][] = [
        [
            {
                code: PaxType.Adult,
                title: 'بزرگسال',
                subtitle: 'بزرگتر از ۱۲ سال',
                count: 2,
            },
            {
                code: PaxType.Child,
                title: 'کودک',
                subtitle: 'بین ۲ الی ۱۲ سال',
                count: 0,
            },
            {
                code: PaxType.Infant,
                title: 'نوزاد',
                subtitle: 'کوچکتر از ۲ سال',
                count: 0,
            },
        ],
    ];

    mapPaxesToRoomsQuery(paxes: PaxCount[][]) {
        return paxes.reduce((query: string, px, i) => {
            if (i > 0) {
                query += ',';
            }
            return (
                query +
                px.reduce((pList: string, p, pi) => {
                    if (pi > 0) {
                        pList += '_';
                    }
                    return pList + p.count;
                }, '')
            );
        }, '');
    }

    override saveSearch(): void {
        if (!this.origin || !this.destination) {
            return;
        }
        // Removing existing
        let existingIndex = this.lastSearches.findIndex(
            (l: Location) => l.code === this.origin!.code
        );
        if (existingIndex === -1) {
            this.lastSearches = [...this.lastSearches, this.origin];
        }
        existingIndex = this.lastSearches.findIndex(
            (l: Location) => l.code === this.destination!.code
        );
        if (existingIndex === -1) {
            this.lastSearches = [...this.lastSearches, this.destination];
        }
        // Checking for the limit
        if (this.lastSearches.length > 3) {
            this.lastSearches = this.lastSearches.slice(0, 3);
        }
        localStorage.setItem(
            this.lastSearchKey,
            JSON.stringify({
                origin: this.origin,
                destination: this.destination,
                fromDate: this.fromDate,
                toDate: this.toDate,
                lastSearches: this.lastSearches,
                rooms: this.mapPaxesToRoomsQuery(this.roomsPaxCounts),
            })
        );
    }

    override verifySearchForm(currentUrl: string): Promise<void> {
        let error;
        if (!this.origin || !this.destination) {
            error = 'مبدا و مقصد نمی‌تواند خالی باشد.';
        } else if (this.origin.code === this.destination.code) {
            error = 'مبدا و مقصد می بایست متفاوت انتخاب شوند.';
        } else if (!this.fromDate) {
            error = 'تاریخ رفت را انتخاب کنید.';
        } else if (!this.toDate) {
            error = 'تاریخ برگشت را انتخاب کنید.';
        } else if (
            dayConfig.gregory(this.fromDate).isAfter(this.toDate, 'day')
        ) {
            error = 'تاریخ رفت نمی‌تواند قبل از تاریخ بازگشت باشد.';
        }

        if (error) {
            return Promise.reject(error);
        }
        if (currentUrl && this.duplicateViewPath(currentUrl)) {
            return Promise.reject(
                new Error(SearchFromErrorMessage.DuplicatePath)
            );
        }
        return Promise.resolve();
    }
}
